import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import routes from '../../../Utils/routes';
import { useStyles } from '../styles';
import { Collapse, ListItemButton, ListItemIcon, ListItemText, useMediaQuery } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useContext, useState } from 'react';
import { AuthContext } from '../../../Helpers/Contexts/authContext';
import RefIcon from '../../../GlobalComponents/RefIcon';

interface SideNavProps {
  handleListClick: (link: string, index: number) => void;
  showLabelsOnSmallScreens?: boolean;
}

interface NavLeaf {
  icon: (isActive: boolean) => JSX.Element;
  label: string;
  link: string;
  name: string;
  facultyOnly?: boolean;
  studentOnly?: boolean;
  // Inner pages reached from this nav item that live at an unrelated path
  // (not a sub-path of `link`) — e.g. clicking into a course from "My
  // Courses" navigates to /academics/course/:id, not /academics/Activities
  // and Resources/*. Listing them here keeps the sidebar highlighted while
  // viewing them.
  alsoActiveFor?: string[];
}

interface NavGroup {
  icon: (isActive: boolean) => JSX.Element;
  label: string;
  name: string;
  facultyOnly?: boolean;
  studentOnly?: boolean;
  items: NavLeaf[];
}

type NavEntry = NavLeaf | NavGroup;

function isGroup(entry: NavEntry): entry is NavGroup {
  return 'items' in entry;
}

const SideNavTabs: React.FunctionComponent<SideNavProps> = ({ handleListClick, showLabelsOnSmallScreens = false }) => {
  const classes = useStyles();
  const { pathname } = useLocation();
  const { state: { user } } = useContext(AuthContext);
  const isFaculty = (user as any)?.Type?.toLowerCase() === 'faculty';
  const isStudent = (user as any)?.Type?.toLowerCase() === 'student';
  // Route paths with spaces/special characters (e.g. '/My Schedule',
  // '/Service & Support') can reach here either decoded or still
  // percent-encoded depending on how they were navigated to — decode
  // defensively so the comparison isn't sensitive to that.
  const safeDecode = (value: string) => {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  };
  const normalizedPathname = safeDecode(pathname).toLowerCase().replace(/\/+$/, '') || '/';
  const matchesPath = (link: string) => {
    // Route templates like '/academics/course/:courseId' have no real match
    // against an actual pathname — compare against the static prefix only.
    const staticPrefix = link.split('/:')[0];
    const normalizedLink = safeDecode(staticPrefix).toLowerCase().replace(/\/+$/, '');
    return normalizedPathname === normalizedLink || normalizedPathname.startsWith(`${normalizedLink}/`);
  };
  const isLinkActive = (link: string, alsoActiveFor?: string[]) =>
    matchesPath(link) || (alsoActiveFor?.some((path) => matchesPath(path)) ?? false);

  const is1980 = useMediaQuery('(max-width:1280px)');
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});

  const toggleGroup = (name: string) => {
    setOpenGroups((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const navIcon = (name: string) => (isActive: boolean) => (
    <RefIcon name={name} size={18} className={isActive ? classes.listItemIconActive : classes.listItemIcon} />
  );

  const allSideNavEntries: NavEntry[] = [
    {
      icon: navIcon('home'),
      label: 'Home',
      link: routes.home,
      name: 'home',
    },
    {
      icon: navIcon('book'),
      label: 'My Batch',
      link: routes.studentPortalCourses,
      name: 'studentPortalCourses',
      studentOnly: true,
      alsoActiveFor: [routes.studentPortalCourseContent],
    },
    {
      icon: navIcon('doc'),
      label: 'Activities',
      link: routes.studentPortalActivities,
      name: 'studentPortalActivities',
      studentOnly: true,
    },
    {
      icon: navIcon('award'),
      label: 'Certificates',
      link: routes.studentPortalCertificates,
      name: 'studentPortalCertificates',
      studentOnly: true,
    },
    {
      icon: navIcon('check'),
      label: 'My Attendance',
      link: routes.studentPortalAttendance,
      name: 'studentPortalAttendance',
      studentOnly: true,
    },
    {
      icon: navIcon('handshake'),
      label: 'Mentoring',
      link: routes.studentPortalMentoring,
      name: 'studentPortalMentoring',
      studentOnly: true,
    },
    {
      icon: navIcon('chat'),
      label: 'Discussion Forum',
      link: routes.studentPortalForum,
      name: 'studentPortalForum',
      studentOnly: true,
    },
    {
      icon: navIcon('grid'),
      label: 'Timetable',
      link: routes.studentPortalTimetable,
      name: 'studentPortalTimetable',
      studentOnly: true,
    },
    {
      icon: navIcon('book'),
      label: 'My Batch',
      link: routes.courseElaborate,
      name: 'courseElaborate',
      facultyOnly: true,
      alsoActiveFor: [routes.courseDetail],
    },
    {
      icon: navIcon('users'),
      label: 'My Students',
      link: routes.myStudents,
      name: 'myStudents',
      facultyOnly: true,
    },
    {
      icon: navIcon('clip'),
      label: 'Activities',
      link: routes.AssessmentAdding,
      name: 'Assessment',
      facultyOnly: true,
    },
    {
      icon: navIcon('calchk'),
      label: 'Attendance',
      link: routes.attendance,
      name: 'attendance',
      facultyOnly: true,
    },
    {
      icon: navIcon('handshake'),
      label: 'Mentoring',
      link: routes.mentoring,
      name: 'mentoring',
      facultyOnly: true,
    },
    {
      icon: navIcon('chat'),
      label: 'Discussion Forum',
      link: routes.ForumDiscussion,
      name: 'Forum',
      facultyOnly: true,
    },
    {
      icon: navIcon('cal'),
      label: 'Timetable',
      link: routes.mySchedule,
      name: 'mySchedule',
      facultyOnly: true,
    },
    {
      icon: navIcon('help'),
      label: 'Support Services',
      name: 'service',
      facultyOnly: true,
      items: [
        {
          icon: navIcon('help'),
          label: 'Raise a Case',
          link: routes.service,
          name: 'Service & Support',
        },
      ],
    },
    {
      icon: navIcon('logout'),
      label: 'Logout',
      link: routes.login,
      name: 'logout',
    },
  ];

  const visibleEntries = allSideNavEntries.filter(
    (entry) => (isFaculty || !entry.facultyOnly) && (isStudent || !entry.studentOnly)
  );

  const renderLeaf = (entry: NavLeaf, index: number, indent = false) => {
    const isActive = isLinkActive(entry.link, entry.alsoActiveFor);
    return (
      <ListItemButton
        key={entry.name}
        onClick={() => handleListClick(entry.link, index)}
        style={{ marginTop: '1px', paddingLeft: indent ? 32 : undefined }}
        className={`${classes.listItemButton} ${isActive && classes.listItemButtonActive}`}
      >
        <ListItemIcon sx={{ minWidth: { xs: '10px', lg: '30px' } }}>{entry.icon(isActive)}</ListItemIcon>
        <ListItemText
          className={`${classes.listItemText} ${isActive && classes.listItemTextActive}`}
          sx={{ display: showLabelsOnSmallScreens ? 'block' : { xs: 'none', sm: 'none', md: 'none', lg: 'block' } }}
        >
          {entry.label}
        </ListItemText>
      </ListItemButton>
    );
  };

  const renderEntry = (entry: NavEntry, index: number) => {
    if (!isGroup(entry)) {
      return renderLeaf(entry, index);
    }
    const isOpen = openGroups[entry.name] ?? false;
    const groupActive = entry.items.some((child) => isLinkActive(child.link, child.alsoActiveFor));
    return (
      <div key={entry.name}>
        <ListItemButton
          onClick={() => toggleGroup(entry.name)}
          style={{ marginTop: '1px' }}
          className={`${classes.listItemButton} ${groupActive && !isOpen && classes.listItemButtonActive}`}
        >
          <ListItemIcon sx={{ minWidth: { xs: '10px', lg: '30px' } }}>{entry.icon(groupActive && !isOpen)}</ListItemIcon>
          <ListItemText
            className={`${classes.listItemText} ${groupActive && !isOpen && classes.listItemTextActive}`}
            sx={{ display: showLabelsOnSmallScreens ? 'block' : { xs: 'none', sm: 'none', md: 'none', lg: 'block' } }}
          >
            {entry.label}
          </ListItemText>
          {isOpen ? <ExpandLessIcon className={classes.listItemIcon} /> : <ExpandMoreIcon className={classes.listItemIcon} />}
        </ListItemButton>
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          {entry.items.map((child, childIndex) => renderLeaf(child, index * 100 + childIndex, true))}
        </Collapse>
      </div>
    );
  };

  return (
    <>
      {is1980 ? (
        <div style={{ overflow: 'auto', height: is1980 ? `calc(100vh - 220px)` : `calc(100vh - 270px)` }} className="scrollbar-cusgray">
          {visibleEntries.map((entry, index) => renderEntry(entry, index))}
        </div>
      ) : (
        <>
          <div style={{ overflow: 'auto', height: 'calc(100vh - 260px)' }} className="scrollbar-cusgray">
            {visibleEntries.slice(0, -1).map((entry, index) => renderEntry(entry, index))}
          </div>
          <div>
            {visibleEntries.slice(-1).map((entry, index) => (
              <div key={entry.name} style={{ position: 'absolute', bottom: 0, marginBottom: '64px', width: '100%' }}>
                {renderEntry(entry, index)}
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
};

export default SideNavTabs;
